import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import { Toaster } from './components/ui/sonner';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import ClientDetail from './pages/ClientDetail';
import Transactions from './pages/Transactions';
import Users from './pages/Users';
import Offices from './pages/Offices';
import Affiliates from './pages/Affiliates';
import Analytics from './pages/Analytics';
import Money from './pages/Money';
import Targets from './pages/Targets';
import Settings from './pages/Settings';
import Documents from './pages/Documents';
import Calendar from './pages/Calendar';
import Hierarchy from './pages/Hierarchy';
import NotFound from './pages/NotFound';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import api from './services/api';
import { toast } from 'sonner';
import { userSettingsService } from './services/userSettings';
import { clearCurrentUserCache, clearPermissionsCache, clearRoleConfigCache, getCurrentUser, hasAnyPermission, hasPermission, setCurrentUserCache, setPermissionsCache, setRoleConfigCache } from './utils/roles';

const routePermissions = [
  { match: path => path === '/dashboard' || path.startsWith('/dashboard/'), permission: 'View Dashboard' },
  { match: path => path === '/clients', permission: 'View Clients' },
  { match: path => path.startsWith('/clients/'), permission: 'View Client Page' },
  { match: path => path === '/transactions' || path.startsWith('/transactions/'), permission: 'View Transactions' },
  { match: path => path === '/documents' || path.startsWith('/documents/'), permission: 'View Documents' },
  { match: path => path === '/calendar' || path.startsWith('/calendar/'), permission: 'View Calendar' },
  { match: path => path === '/offices' || path.startsWith('/offices/'), permission: 'View Offices' },
  { match: path => path === '/users' || path.startsWith('/users/'), permission: 'View Users' },
  { match: path => path === '/analytics' || path.startsWith('/analytics/'), permission: 'View Analytics' },
  { match: path => path === '/affiliates' || path.startsWith('/affiliates/'), permission: 'View Affiliates' },
  { match: path => path === '/money' || path.startsWith('/money/') || path === '/targets' || path.startsWith('/targets/'), permission: 'View Finance' },
  { match: path => path === '/settings' || path.startsWith('/settings/'), permission: 'View Settings' },
  { match: path => path === '/hierarchy' || path.startsWith('/hierarchy/'), any: ['View Users', 'View Offices'] },
];

const landingRoutes = [
  { path: '/dashboard', permission: 'View Dashboard' },
  { path: '/clients', permission: 'View Clients' },
  { path: '/transactions', permission: 'View Transactions' },
  { path: '/calendar', permission: 'View Calendar' },
  { path: '/documents', permission: 'View Documents' },
  { path: '/offices', permission: 'View Offices' },
  { path: '/users', permission: 'View Users' },
  { path: '/analytics', permission: 'View Analytics' },
  { path: '/affiliates', permission: 'View Affiliates' },
  { path: '/settings', permission: 'View Settings' },
];

const getAllowedPath = (user) => {
  const route = landingRoutes.find(item => !item.permission || hasPermission(user, item.permission));
  return route?.path || '/404';
};

const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [authContextLoaded, setAuthContextLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setAuthContextLoaded(false);
    setIsAuthenticated(null);
    Promise.all([
      api.get('/auth/me'),
      api.get('/permissions/me'),
      api.get('/app/config').catch(() => ({ data: null })),
    ])
      .then(([meRes, permissionsRes, configRes]) => {
        if (cancelled) return;
        setCurrentUserCache(meRes.data || {});
        setPermissionsCache(permissionsRes.data?.permissions || {});
        setRoleConfigCache(configRes.data);
        setIsAuthenticated(true);
      })
      .catch(() => {
        if (!cancelled) {
          clearCurrentUserCache();
          clearPermissionsCache();
          clearRoleConfigCache();
          setIsAuthenticated(false);
        }
      })
      .finally(() => {
        if (!cancelled) setAuthContextLoaded(true);
      });

    return () => { cancelled = true; };
  }, []);

  if (!authContextLoaded || isAuthenticated === null) return <div className="min-h-screen bg-primary" />;
  if (!isAuthenticated) return <Navigate to="/" replace />;

  try {
    const user = getCurrentUser();
    const path = location.pathname;
    const routeRule = routePermissions.find(rule => rule.match(path));
    if (routeRule?.permission && !hasPermission(user, routeRule.permission)) return <Navigate to={getAllowedPath(user)} replace />;
    if (routeRule?.any && !hasAnyPermission(user, routeRule.any)) return <Navigate to={getAllowedPath(user)} replace />;
  } catch {}

  return children;
};

const REMINDER_POLL_MS = 60_000; // check every 60 seconds
const SIDEBAR_COLLAPSED_SETTING_KEY = 'layout.sidebarCollapsed';

const MainLayout = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const shownIds = useRef(new Set());

  useEffect(() => {
    let cancelled = false;
    userSettingsService.get(SIDEBAR_COLLAPSED_SETTING_KEY, false).then(value => {
      if (!cancelled) {
        setIsSidebarCollapsed(value === true);
      }
    });
    return () => { cancelled = true; };
  }, []);

  const toggleSidebarCollapsed = () => {
    setIsSidebarCollapsed(value => {
      const next = !value;
      userSettingsService.set(SIDEBAR_COLLAPSED_SETTING_KEY, next).catch(() => {});
      return next;
    });
  };

  useEffect(() => {
    let canViewCalendar = false;
    try {
      canViewCalendar = hasPermission(getCurrentUser(), 'View Calendar');
    } catch {}
    if (!canViewCalendar) return undefined;

    const check = async () => {
      try {
        const { data } = await api.get('/calendar/upcoming');
        (data || []).forEach(ev => {
          if (shownIds.current.has(ev.id)) return;
          shownIds.current.add(ev.id);

          const typeLabel = { call: 'Call', reminder: 'Reminder', meeting: 'Meeting', other: 'Event' }[ev.type] || 'Event';
          const timeStr = ev.allDay ? 'All day' : new Date(ev.startAt.endsWith('Z') || /[+\-]\d{2}:\d{2}$/.test(ev.startAt) ? ev.startAt : ev.startAt + 'Z').toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
          const client = ev.clientName ? ` - ${ev.clientName}` : '';

          toast(`${typeLabel}: ${ev.title}${client}`, {
            description: `Starts at ${timeStr} - in about 5 minutes`,
            duration: 20000,
            action: { label: 'Open Calendar', onClick: () => window.location.assign('/calendar') },
          });
        });
      } catch { /* silently skip if not authed */ }
    };

    check();
    const timer = setInterval(check, REMINDER_POLL_MS);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="crm-shell flex h-screen bg-primary overflow-hidden">
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={toggleSidebarCollapsed}
      />
      <div className="min-w-0 flex-1 flex flex-col overflow-hidden">
        <Header onMenuClick={() => setIsSidebarOpen(true)} />
        <main className="crm-main-scroll min-w-0 flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <div className="App">
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Login />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Dashboard />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/clients"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Clients />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/clients/:id"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <ClientDetail />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/documents"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Documents />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/offices"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Offices />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/users"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Users />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Analytics />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/affiliates"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Affiliates />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/transactions"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Transactions />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/money"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Money />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/targets"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Targets />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Settings />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/calendar"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Calendar />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/hierarchy"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Hierarchy />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="*"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <NotFound />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </BrowserRouter>
          <Toaster />
        </div>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
